import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Button, IconButton, MenuItem, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { compactDuration, DurationMode, DurationParts, durationUnits, formatDurationAmount, formatDurationParts, parseDuration } from './timeSpanValue';

type TimeSpanInputProps = {
	value: string | null | undefined;
	onValueChange: (value: string | null) => void;
	optional?: boolean;
	disabled?: boolean;
	emptyLabel?: string;
};

const zeroParts: DurationParts = { days: '0', hours: '0', minutes: '0', seconds: '0' };

export default function TimeSpanInput({ value, onValueChange, optional = false, disabled, emptyLabel = 'Not set' }: TimeSpanInputProps) {
	const [compact, setCompact] = useState(() => compactDuration(value, optional));
	const lastValue = useRef(value);
	const root = useRef<HTMLDivElement>(null);
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const [parts, setParts] = useState<DurationParts>(zeroParts);
	const customValue = formatDurationParts(parts);
	const amountInvalid = compact.mode !== 'custom' && compact.mode !== 'none' && formatDurationAmount(compact.amount, compact.mode) === undefined;

	useEffect(() => {
		if (value !== lastValue.current) {
			lastValue.current = value;
			setCompact(compactDuration(value, optional));
			setAnchor(null);
		}
	}, [value, optional]);

	function emit(next: string | null) {
		lastValue.current = next;
		onValueChange(next);
	}

	function editCustom() {
		setParts(parseDuration(value) ?? zeroParts);
		setAnchor(root.current);
	}

	function changeMode(mode: DurationMode) {
		if (mode === 'custom') {
			editCustom();
			return;
		}
		if (mode === 'none') {
			setCompact({ ...compact, mode });
			emit(null);
			return;
		}
		const amount = compact.mode === 'custom' || compact.mode === 'none' ? '1' : compact.amount;
		setCompact({ amount, mode });
		const formatted = formatDurationAmount(amount, mode);
		if (formatted !== undefined) emit(formatted);
	}

	return (
		<Box ref={root} sx={{ width: '100%', minWidth: 0, mt: 1 }}>
			<Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ minWidth: 0 }}>
				{compact.mode !== 'none' && (
					<TextField
						variant="standard"
						size="small"
						type={compact.mode === 'custom' ? 'text' : 'number'}
						value={compact.mode === 'custom' ? value ?? '' : compact.amount}
						disabled={disabled}
						error={amountInvalid}
						helperText={amountInvalid ? 'Enter a whole, nonnegative duration within the TimeSpan range.' : undefined}
						onClick={() => { if (!disabled && compact.mode === 'custom') editCustom(); }}
						onChange={event => {
							if (compact.mode === 'custom' || compact.mode === 'none') return;
							setCompact({ ...compact, amount: event.target.value });
							const formatted = formatDurationAmount(event.target.value, compact.mode);
							if (formatted !== undefined) emit(formatted);
						}}
						slotProps={{ input: { readOnly: compact.mode === 'custom' }, htmlInput: { 'aria-label': compact.mode === 'custom' ? 'Duration' : 'Value', min: 0, step: 1, title: compact.mode === 'months' ? '1 month = 30 days' : undefined } }}
						sx={{ flex: '1 1 0', minWidth: 0 }}
					/>
				)}
				<TextField select variant="standard" size="small" value={compact.mode} disabled={disabled}
					slotProps={{ select: { inputProps: { 'aria-label': 'Unit' } } }}
					onChange={event => changeMode(event.target.value as DurationMode)}
					sx={{ flex: '1 1 0', minWidth: 0 }}>
					{optional && <MenuItem value="none">{emptyLabel}</MenuItem>}
					{durationUnits.map(unit => <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>)}
					<MenuItem value="custom">Custom…</MenuItem>
				</TextField>
				<Tooltip title="Edit custom duration">
					<span style={{ display: 'inline-flex', height: 32, alignItems: 'center' }}><IconButton size="small" aria-label="Edit custom duration" disabled={disabled} onClick={editCustom}>
						<MoreHorizIcon />
					</IconButton></span>
				</Tooltip>
			</Stack>
			<Popover open={!!anchor && !disabled} anchorEl={anchor} onClose={() => setAnchor(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { p: 2, width: 340, maxWidth: 'calc(100vw - 32px)' } } }}>
				<Typography variant="subtitle2" sx={{ mb: 2 }}>Custom duration</Typography>
				<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
					{(['days', 'hours', 'minutes', 'seconds'] as const).map(field => <TextField key={field}
						label={field[0].toUpperCase() + field.slice(1)} type="number" size="small" value={parts[field]}
						onChange={event => setParts({ ...parts, [field]: event.target.value })}
						slotProps={{ htmlInput: { min: 0, max: field === 'hours' ? 23 : field === 'minutes' || field === 'seconds' ? 59.9999999 : undefined, step: field === 'seconds' ? 'any' : 1 } }} />)}
				</Box>
				<Typography variant="caption" color={customValue ? 'text.secondary' : 'error'} sx={{ display: 'block', mt: 1.5 }}>
					{customValue ? `TimeSpan: ${customValue}` : 'Use nonnegative values: hours 0–23, minutes and seconds below 60.'}
				</Typography>
				<Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
					<Button size="small" onClick={() => setAnchor(null)}>Cancel</Button>
					<Button size="small" variant="contained" disabled={!customValue || disabled} onClick={() => {
						if (!customValue) return;
						setCompact(compactDuration(customValue, optional));
						emit(customValue);
						setAnchor(null);
					}}>Apply</Button>
				</Stack>
			</Popover>
		</Box>
	);
}
